import React, { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import Breadcrum from '../../components/breadcrum'
import AddItem from '../../components/addItem'
import { FaCopy, FaEdit, FaTrash } from 'react-icons/fa'
import { Link } from 'react-router-dom'
import { allTrending, deleteTranding, sendMail } from '../../api/trendingApi'
import toast from 'react-hot-toast'
import { handleDate } from '../../handleDateAndPdf'
import Swal from 'sweetalert2'

const TrendingNews = () => {
  const queryClient = useQueryClient()
  const [toDelete, setToDelete] = useState(null)
  const [sendingMail, setSendingMail] = useState(false)
  const [expand, setExpand] = useState(false)

  useEffect(() => {
    document.title = 'Trending News'
  }, [])

  const {
    data: trendingData = [],
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['trendingNews'], // Array format recommended
    queryFn: allTrending, // Your API call function
    onError: (error) => {
      console.error('Error fetching trending news:', error)
    },
  })

  const total = trendingData?.length || 0

  // Send mail mutation with improved loading state
  const sendMailMutation = useMutation({
    mutationFn: (id) => sendMail(id),
    onMutate: () => {
      setSendingMail(true)
    },
    onSuccess: (response) => {
      setSendingMail(false)

      // Check if the response indicates success
      if (response?.status === 200 || response?.data) {
        toast.success(response?.data)
        // Show OTP modal after successful email send
        showOTPModal()
      } else {
        toast.error('Failed to send verification email')
        setToDelete(null)
      }
    },
    onError: (err) => {
      setSendingMail(false)
      console.error('Send mail error:', err)
      toast.error(err.message || 'Failed to send verification email')
      setToDelete(null)
    },
  })

  // Delete mutation with improved success handling
  const deleteMutation = useMutation({
    mutationFn: ({ id, otp }) => deleteTranding(id, otp),
    onSuccess: (response) => {
      // Invalidate and refetch trendings
      queryClient.invalidateQueries({ queryKey: ['trendingNews'] })

      // Show success message
      toast.success('trending deleted successfully!')

      // Additional success message with SweetAlert
      Swal.fire({
        title: 'Deleted!',
        text: 'The trending has been deleted successfully.',
        icon: 'success',
        confirmButtonColor: '#3085d6',
        timer: 3000,
        timerProgressBar: true,
      })

      // Reset state
      setToDelete(null)
    },
    onError: (err) => {
      console.error('Delete error:', err)
      const errorMessage =
        err?.response?.data?.message ||
        err?.message ||
        'Failed to delete trending. Please check your OTP.'
      toast.error(errorMessage)
    },
  })

  // Show OTP modal using SweetAlert with better UX
  const showOTPModal = () => {
    Swal.fire({
      title: 'Enter OTP',
      html: `
        <p>Please enter the OTP sent to your email</p>
        <div style="margin: 10px 0; padding: 10px; background-color: #f0f9ff; border-radius: 8px; border-left: 4px solid #3b82f6;">
          <small style="color: #1e40af;">
            <i class="fas fa-info-circle"></i> Check your email inbox and spam folder
          </small>
        </div>
      `,
      input: 'number',
      inputAttributes: {
        autocapitalize: 'off',
        placeholder: 'Enter 6-digit OTP',
        maxlength: 6,
        style: 'text-align: center; font-size: 18px; letter-spacing: 2px;',
      },
      showCancelButton: true,
      confirmButtonText: 'Delete trending',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#6b7280',
      allowOutsideClick: false,
      inputValidator: (value) => {
        if (!value) {
          return 'Please enter OTP'
        }
        if (value.length !== 6) {
          return 'OTP must be 6 digits'
        }
        if (!/^\d+$/.test(value)) {
          return 'OTP must contain only numbers'
        }
      },
      preConfirm: (otp) => {
        return new Promise((resolve, reject) => {
          // Show loading state
          Swal.showLoading()

          // Update confirm button text while loading
          const confirmButton = Swal.getConfirmButton()
          const originalText = confirmButton.textContent
          confirmButton.textContent = 'Deleting...'

          // Call delete mutation with OTP
          deleteMutation.mutate(
            { id: toDelete, otp },
            {
              onSuccess: () => {
                resolve()
                Swal.close()
              },
              onError: (error) => {
                confirmButton.textContent = originalText
                Swal.hideLoading()
                const errorMessage =
                  error?.response?.data?.message ||
                  error?.message ||
                  'Invalid OTP or deletion failed'
                Swal.showValidationMessage(`❌ ${errorMessage}`)
                reject(error)
              },
            }
          )
        })
      },
    })
      .then((result) => {
        if (result.dismiss === Swal.DismissReason.cancel) {
          setToDelete(null)
          toast.info('Deletion cancelled')
        }
      })
      .catch(() => {
        // Handle any unexpected errors
        setToDelete(null)
      })
  }

  // Handle delete click with improved UX
  const handleDeleteClick = (trendingId, trendingTitle) => {
    setToDelete(trendingId)

    // Show confirmation dialog first
    Swal.fire({
      title: 'Delete trending?',
      html: `
        <p>Are you sure you want to delete this trending?</p>
        <div style="margin: 10px 0; padding: 10px; background-color: #fef2f2; border-radius: 8px; border-left: 4px solid #ef4444;">
          <strong style="color: #dc2626;">${trendingTitle}</strong>
        </div>
        <p style="color: #6b7280; font-size: 14px;">
          This action will send an OTP to your email for verification.
        </p>
      `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, Send OTP',
      cancelButtonText: 'Cancel',
      allowOutsideClick: false,
    }).then((result) => {
      if (result.isConfirmed) {
        // Send email for OTP
        sendMailMutation.mutate(trendingId)
      } else {
        setToDelete(null)
      }
    })
  }

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text)
    toast.success('text copied !')
  }

  if (isLoading) {
    return (
      <>
        <Breadcrum heading="Trending" subheading="News" />
        <div className="text-center py-8">Loading trending news...</div>
      </>
    )
  }

  if (isError) {
    return (
      <>
        <Breadcrum heading="Trending" subheading="News" />
        <div className="text-center py-8 text-red-500">
          Error loading trending news. Please try again later.
        </div>
      </>
    )
  }

  console.log('trendingData', trendingData)

  return (
    <>
      <Breadcrum heading="Trending" subheading="News" />

      <AddItem redirect="/dealdashboard/add/trending" />

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 grid-flow-row text-left sm:w-[90%] mx-auto gap-6 mt-8">
        <div className="fixed right-10 font-aptos-regular top-[18%] bg-[#ff7010] rounded-full text-white text-xl w-fit h-fit px-2 py-1 shadow-md">
          {total}
        </div>

        {trendingData?.map((d, index) => (
          <div key={index} className=" bg-white rounded p-6 last:mb-0 h-fit">
            <p className="text-black text-lg font-aptos-semibold mb-3 inline-block">
              {d.title}
            </p>
            <img
              src={d.imageUrl}
              alt="img"
              className="w-full h-60 object-cover rounded-md mb-3 border-gray-400 border-2"
            />

            <p
              className={`text-gray-800 font-aptos-semibold text-sm ${
                expand === d.id ? '' : 'line-clamp-6'
              } `}
            >
              {d.description}
            </p>

            {d.id === expand ? (
              <p
                className="text-[#e15d05] font-aptos-semibold cursor-pointer float-right"
                onClick={() => setExpand(false)}
              >
                View less
              </p>
            ) : (
              <p
                className="text-[#e15d05] font-aptos-semibold cursor-pointer float-right"
                onClick={() => setExpand(d.id)}
              >
                View full
              </p>
            )}

            <div className="my-5 font-aptos-regular">
              <p>Redirect Url</p>
              <div className="flex items-center justify-left bg-gray-100 p-2 rounded mb-3 w-fit">
                <span className="text-sm text-gray-500 break-all">
                  {d.pageUrl}
                </span>
                <button
                  onClick={() => handleCopy(d.pageUrl)}
                  className="text-gray-600 hover:text-orange-500 ml-2"
                >
                  <FaCopy />
                </button>
              </div>

              <p>Tag Url</p>
              <div className="flex items-center justify-left bg-gray-100 p-2 rounded mb-3 w-fit">
                <span className="text-sm text-gray-500 break-all">
                  {d.tagUrl}
                </span>
                <button
                  onClick={() => handleCopy(d.tagUrl)}
                  className="text-gray-600 hover:text-orange-500 ml-2"
                >
                  <FaCopy />
                </button>
              </div>
            </div>

            <div className="flex justify-between font-aptos-regular mb-3 gap-3 items-center">
              <div>
                <p>Created At</p>
                <div className="flex items-center justify-left bg-gray-100 p-2 rounded mb-3 w-fit">
                  <span className="text-sm text-gray-500 break-all">
                    {handleDate(d.createdAt)}
                  </span>
                </div>

                <p>Created By</p>
                <div className="flex items-center justify-left bg-gray-100 p-2 rounded mb-3 w-fit">
                  <span className="text-sm text-gray-500 break-all">
                    {d?.createAdmin}
                  </span>
                </div>
              </div>
              {d.updatedAt && (
                <div className="mb-3">
                  <p>Updated At</p>
                  <div className="flex items-center justify-left bg-gray-100 p-2 rounded mb-3 w-fit">
                    <span className="text-sm text-gray-500 break-all">
                      {handleDate(d.updatedAt)}
                    </span>
                  </div>

                  <p>Updated By</p>
                  <div className="flex items-center justify-left bg-gray-100 p-2 rounded mb-3 w-fit">
                    <span className="text-sm text-gray-500 break-all">
                      {d?.updateAdmin}
                    </span>
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center font-aptos-regular justify-between space-x-4">
              <Link
                to={`/dealdashboard/add/trending/${d.id}`} // Assuming each item has an id
                className="text-green-600 hover:text-green-800 flex items-center"
              >
                <FaEdit className="mr-1" /> Edit
              </Link>
              <button
                type="button"
                onClick={() => handleDeleteClick(d.id, d.title)}
                className={`text-red-500 hover:text-red-700 transition-colors p-1 ${
                  sendingMail || deleteMutation.isPending
                    ? 'opacity-50 cursor-not-allowed'
                    : ''
                }`}
                title="Delete trending"
                disabled={sendingMail || deleteMutation.isPending}
              >
                {sendingMail && toDelete === d.id ? (
                  <div className="animate-spin h-4 w-4 border-2 border-red-500 border-t-transparent rounded-full"></div>
                ) : (
                  <FaTrash />
                )}
              </button>
            </div>
          </div>
        ))}
        {/* Loading overlay for sending email */}
        {sendingMail && (
          <div className="fixed inset-0 font-aptos-regular bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg shadow-xl text-center">
              <div className="animate-spin h-8 w-8 border-4 border-orange-500 border-t-transparent rounded-full mx-auto mb-4"></div>
              <p className="text-lg font-semibold">
                Sending verification email...
              </p>
              <p className="text-sm text-gray-500 mt-1">Please wait</p>
            </div>
          </div>
        )}
      </div>

      <AddItem redirect="/dealdashboard/add/trending" />
    </>
  )
}

export default TrendingNews
